import { useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

export default function OwnerAccount() {
  const { user, setUser } = useAuth();

  const [email, setEmail] = useState(user?.email || "");
  const [name, setName] = useState(user?.name || "");
  const [savingAccount, setSavingAccount] = useState(false);

  const [currentPw, setCurrentPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [savingPw, setSavingPw] = useState(false);

  const saveAccount = async (e) => {
    e.preventDefault();
    const cleanEmail = email.trim().toLowerCase();
    if (!cleanEmail) {
      toast.error("Email is required");
      return;
    }
    setSavingAccount(true);
    try {
      const { data } = await api.put("/me/account", { email: cleanEmail, name: name.trim() || null });
      setUser(data.user);
      toast.success(data.updated ? "Account updated" : "Nothing to change");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update");
    } finally {
      setSavingAccount(false);
    }
  };

  const savePassword = async (e) => {
    e.preventDefault();
    if (newPw.length < 6) {
      toast.error("New password must be at least 6 characters");
      return;
    }
    if (newPw !== confirmPw) {
      toast.error("New passwords don't match");
      return;
    }
    setSavingPw(true);
    try {
      await api.put("/me/password", { current_password: currentPw, new_password: newPw });
      toast.success("Password changed");
      setCurrentPw(""); setNewPw(""); setConfirmPw("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not change password");
    } finally {
      setSavingPw(false);
    }
  };

  return (
    <div className="space-y-8 ff-fade-in max-w-2xl" data-testid="owner-account-page">
      <header>
        <div className="ff-label">Account</div>
        <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight mt-2" style={{ fontFamily: "Outfit, sans-serif" }}>
          Your owner account
        </h1>
        <p className="text-[#5C6B61] mt-2 max-w-xl">
          Change your sign-in email or password. Your admin access is tied to your account, not your email — so updating it won&apos;t lock you out.
        </p>
      </header>

      <form onSubmit={saveAccount} className="ff-card p-6 md:p-8 space-y-5" data-testid="account-form">
        <div>
          <label className="ff-label">Display name</label>
          <input
            className="ff-input mt-1"
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={80}
            data-testid="account-name-input"
            placeholder="Your name"
          />
        </div>
        <div>
          <label className="ff-label">Email / username</label>
          <input
            type="email"
            required
            className="ff-input mt-1"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            data-testid="account-email-input"
          />
          <p className="text-xs text-[#8B9690] mt-1">You&apos;ll sign in with this email next time.</p>
        </div>
        <div className="flex justify-end">
          <button
            type="submit"
            disabled={savingAccount}
            data-testid="account-save-btn"
            className="ff-btn-primary disabled:opacity-60"
          >
            {savingAccount ? "Saving…" : "Save changes"}
          </button>
        </div>
      </form>

      <form onSubmit={savePassword} className="ff-card p-6 md:p-8 space-y-5" data-testid="password-form">
        <h2 className="text-lg font-medium" style={{ fontFamily: "Outfit, sans-serif" }}>Change password</h2>
        <div>
          <label className="ff-label">Current password</label>
          <input
            type="password"
            required
            className="ff-input mt-1"
            value={currentPw}
            onChange={(e) => setCurrentPw(e.target.value)}
            data-testid="password-current-input"
            autoComplete="current-password"
          />
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="ff-label">New password</label>
            <input
              type="password"
              required
              minLength={6}
              className="ff-input mt-1"
              value={newPw}
              onChange={(e) => setNewPw(e.target.value)}
              data-testid="password-new-input"
              autoComplete="new-password"
            />
          </div>
          <div>
            <label className="ff-label">Confirm new password</label>
            <input
              type="password"
              required
              minLength={6}
              className="ff-input mt-1"
              value={confirmPw}
              onChange={(e) => setConfirmPw(e.target.value)}
              data-testid="password-confirm-input"
              autoComplete="new-password"
            />
          </div>
        </div>
        <div className="flex justify-end">
          <button
            type="submit"
            disabled={savingPw}
            data-testid="password-save-btn"
            className="ff-btn-primary disabled:opacity-60"
          >
            {savingPw ? "Saving…" : "Change password"}
          </button>
        </div>
      </form>
    </div>
  );
}
